<template>
  <div>
    <!-- Create step: no sidebar -->
    <game-builder-create
      v-if="currentStep === 'create'"
      ref="createForm"
      @create="onCreateGame"
    />

    <!-- Done step -->
    <b-card v-else-if="currentStep === 'done'">
      <h3 class="mb-1">
        Игра готова!
      </h3>
      <p>Все темы и вопросы заполнены.</p>
      <div class="d-flex" style="gap: 8px">
        <b-button
          variant="success"
          :disabled="isSaving"
          @click="onFinalize"
        >
          <span v-if="isSaving">
            <b-spinner small />
            Финализация...
          </span>
          <span v-else>Финализировать игру</span>
        </b-button>
        <b-button
          variant="primary"
          :disabled="isExporting"
          @click="onExportSiq"
        >
          <span v-if="isExporting">
            <b-spinner small />
            Сборка...
          </span>
          <span v-else>Собрать SIQ файл</span>
        </b-button>
        <b-button
          variant="outline-danger"
          @click="onDeleteGame"
        >
          Удалить игру
        </b-button>
      </div>
    </b-card>

    <!-- Wizard steps with sidebar -->
    <content-with-sidebar
      v-else-if="gameData"
      class="cws-container cws-sidebar-right blog-wrapper"
    >
      <!-- Main content -->
      <div class="blog-detail-wrapper">
        <b-overlay
          :show="isLoading"
          rounded="sm"
        >
          <!-- Progress bar -->
          <b-progress
            :value="progressPercent"
            :max="100"
            variant="primary"
            class="mb-2"
            style="height: 6px"
          />

          <!-- Topic step -->
          <game-builder-topic-step
            v-if="currentStep === 'topic'"
            :suggested-topic="suggestedTopic"
            :available-items="availableItems"
            :is-loading="isLoadingTopic"
            :is-saving="isSaving"
            :current-rt="currentRtObject"
            :game-type="gameData ? gameData.type : null"
            @accepted="onTopicAccepted"
            @skipped="onTopicSkipped"
          />

          <!-- Frame step -->
          <game-builder-frame-step
            v-if="currentStep === 'frames'"
            :game-id="gameId"
            :rt-id="currentRtId"
            :current-rt="currentRtObject"
            :suggested-movie="suggestedMovie"
            :frames-source="framesSource"
            :is-loading-movie="isLoadingMovie"
            :is-saving="isSaving"
            @accepted="onQuestionAccepted"
            @skipped="onMovieSkipped"
            @question-deleted="onQuestionDeleted"
            @topic-removed="onTopicRemoved"
          />
        </b-overlay>
      </div>

      <!-- Sidebar -->
      <template #sidebar>
        <game-builder-progress-sidebar
          :game-data="gameData"
          :current-rt-id="currentRtId"
        />
      </template>
    </content-with-sidebar>

    <!-- Global loading -->
    <div
      v-if="!gameData && currentStep !== 'create'"
      class="text-center py-3"
    >
      <b-spinner variant="primary" />
    </div>
  </div>
</template>

<script>
import {
  BCard, BButton, BOverlay, BProgress, BSpinner,
} from 'bootstrap-vue'
import { ref, computed, onUnmounted } from '@vue/composition-api'
import { useToast } from 'vue-toastification/composition'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import ContentWithSidebar from '@core/layouts/components/content-with-sidebar/ContentWithSidebar.vue'
import router from '@/router'
import store from '@/store'
import gameBuilderStoreModule from './gameBuilderStoreModule'
import GameBuilderCreate from './GameBuilderCreate.vue'
import GameBuilderTopicStep from './GameBuilderTopicStep.vue'
import GameBuilderFrameStep from './GameBuilderFrameStep.vue'
import GameBuilderProgressSidebar from './GameBuilderProgressSidebar.vue'

export default {
  components: {
    BCard,
    BButton,
    BOverlay,
    BProgress,
    BSpinner,
    ContentWithSidebar,
    GameBuilderCreate,
    GameBuilderTopicStep,
    GameBuilderFrameStep,
    GameBuilderProgressSidebar,
  },
  setup() {
    const STORE_MODULE_NAME = 'game-builder'
    const toast = useToast()

    if (!store.hasModule(STORE_MODULE_NAME)) store.registerModule(STORE_MODULE_NAME, gameBuilderStoreModule)
    onUnmounted(() => {
      if (store.hasModule(STORE_MODULE_NAME)) store.unregisterModule(STORE_MODULE_NAME)
    })

    const createForm = ref(null)
    const gameId = ref(router.currentRoute.params.id || null)
    const gameData = ref(null)
    const currentRtId = ref(null)
    const currentStep = ref('create')
    const suggestedTopic = ref(null)
    const availableItems = ref(null)
    const suggestedMovie = ref(null)
    const isLoading = ref(false)
    const isSaving = ref(false)
    const isExporting = ref(false)
    const isLoadingTopic = ref(false)
    const isLoadingMovie = ref(false)

    const isRtComplete = rt => {
      if (!rt.autotopic_id) return false
      const required = rt.required_questions || 5
      return (rt.questions?.length || 0) >= required
    }

    const allRoundTopics = computed(() => {
      if (!gameData.value || !gameData.value.rounds) return []
      const result = []
      gameData.value.rounds.forEach(round => {
        if (round.topics) {
          round.topics.forEach(rt => {
            result.push({ ...rt, round_name: round.name, round })
          })
        }
      })
      return result
    })

    const firstIncompleteRt = computed(() => allRoundTopics.value.find(
      rt => !isRtComplete(rt),
    ) || null)

    const currentRtObject = computed(() => allRoundTopics.value.find(
      rt => rt.id === currentRtId.value,
    ) || {})

    const progressPercent = computed(() => {
      const total = allRoundTopics.value.length
      if (!total) return 0
      const completed = allRoundTopics.value.filter(
        rt => isRtComplete(rt),
      ).length
      return Math.round((completed / total) * 100)
    })

    const fetchSuggestAutotopic = () => {
      isLoadingTopic.value = true
      suggestedTopic.value = null
      store.dispatch('game-builder/suggestAutotopic', {
        gameId: gameId.value,
        rtId: currentRtId.value,
      })
        .then(response => {
          const data = response.data.data || response.data
          suggestedTopic.value = data.autotopic || data
          availableItems.value = data.available_items || null
          isLoadingTopic.value = false
        })
        .catch(error => {
          isLoadingTopic.value = false
          if (error.response && error.response.status === 422) {
            toast({
              component: ToastificationContent,
              props: {
                title: 'Нет доступных автотем',
                icon: 'AlertTriangleIcon',
                variant: 'warning',
              },
            })
          }
        })
    }

    const framesSource = ref(null)

    const fetchSuggestMovie = () => {
      isLoadingMovie.value = true
      suggestedMovie.value = null
      framesSource.value = null
      store.dispatch('game-builder/suggestMovie', {
        gameId: gameId.value,
        rtId: currentRtId.value,
      })
        .then(response => {
          const data = response.data.data || response.data
          framesSource.value = data.frames_source || null
          if (data.movie) {
            suggestedMovie.value = {
              ...data.movie,
              frames: data.frames || [],
            }
          } else {
            suggestedMovie.value = data
          }
          isLoadingMovie.value = false
        })
        .catch(error => {
          isLoadingMovie.value = false
          if (error.response && error.response.status === 422) {
            toast({
              component: ToastificationContent,
              props: {
                title: 'Нет фильмов с кадрами для этой темы',
                icon: 'AlertTriangleIcon',
                variant: 'warning',
              },
            })
          }
        })
    }

    const advanceStep = () => {
      if (!gameData.value) {
        currentStep.value = 'create'
        return
      }
      const incomplete = firstIncompleteRt.value
      if (!incomplete) {
        currentStep.value = 'done'
        currentRtId.value = null
        return
      }
      currentRtId.value = incomplete.id
      if (!incomplete.autotopic_id) {
        currentStep.value = 'topic'
        fetchSuggestAutotopic()
      } else {
        currentStep.value = 'frames'
        fetchSuggestMovie()
      }
    }

    const loadGame = id => {
      isLoading.value = true
      store.dispatch('game-builder/loadGame', { id })
        .then(response => {
          gameData.value = response.data.data || response.data
          isLoading.value = false
          advanceStep()
        })
        .catch(() => {
          isLoading.value = false
          toast({
            component: ToastificationContent,
            props: {
              title: 'Ошибка загрузки игры',
              icon: 'AlertTriangleIcon',
              variant: 'danger',
            },
          })
        })
    }

    // Init
    if (gameId.value) {
      loadGame(gameId.value)
    } else {
      currentStep.value = 'create'
    }

    // Event handlers

    const onCreateGame = payload => {
      isSaving.value = true
      store.dispatch('game-builder/createGame', payload)
        .then(response => {
          const data = response.data.data || response.data
          gameData.value = data
          gameId.value = data.id
          isSaving.value = false
          router.replace({ name: 'game-builder', params: { id: data.id } })
          advanceStep()
        })
        .catch(() => {
          isSaving.value = false
          if (createForm.value) createForm.value.stopLoading()
          toast({
            component: ToastificationContent,
            props: {
              title: 'Ошибка создания игры',
              icon: 'AlertTriangleIcon',
              variant: 'danger',
            },
          })
        })
    }

    const onTopicAccepted = autotopicId => {
      isSaving.value = true
      store.dispatch('game-builder/acceptAutotopic', {
        gameId: gameId.value,
        rtId: currentRtId.value,
        autotopicId,
      })
        .then(() => {
          isSaving.value = false
          loadGame(gameId.value)
        })
        .catch(() => {
          isSaving.value = false
          toast({
            component: ToastificationContent,
            props: {
              title: 'Ошибка принятия темы',
              icon: 'AlertTriangleIcon',
              variant: 'danger',
            },
          })
        })
    }

    const onTopicSkipped = () => {
      fetchSuggestAutotopic()
    }

    const onQuestionAccepted = payload => {
      isSaving.value = true
      store.dispatch('game-builder/acceptQuestion', {
        gameId: gameId.value,
        rtId: currentRtId.value,
        payload,
      })
        .then(() => {
          isSaving.value = false
          loadGame(gameId.value)
        })
        .catch(() => {
          isSaving.value = false
          toast({
            component: ToastificationContent,
            props: {
              title: 'Ошибка сохранения вопроса',
              icon: 'AlertTriangleIcon',
              variant: 'danger',
            },
          })
        })
    }

    const onMovieSkipped = () => {
      fetchSuggestMovie()
    }

    const onQuestionDeleted = questionId => {
      isSaving.value = true
      store.dispatch('game-builder/deleteQuestion', {
        gameId: gameId.value,
        rtId: currentRtId.value,
        questionId,
      })
        .then(() => {
          isSaving.value = false
          loadGame(gameId.value)
        })
        .catch(() => {
          isSaving.value = false
          toast({
            component: ToastificationContent,
            props: {
              title: 'Ошибка удаления вопроса',
              icon: 'AlertTriangleIcon',
              variant: 'danger',
            },
          })
        })
    }

    const onTopicRemoved = () => {
      isSaving.value = true
      store.dispatch('game-builder/removeTopic', {
        gameId: gameId.value,
        rtId: currentRtId.value,
      })
        .then(() => {
          isSaving.value = false
          loadGame(gameId.value)
        })
        .catch(() => {
          isSaving.value = false
          toast({
            component: ToastificationContent,
            props: {
              title: 'Ошибка удаления темы',
              icon: 'AlertTriangleIcon',
              variant: 'danger',
            },
          })
        })
    }

    const onFinalize = () => {
      isSaving.value = true
      store.dispatch('game-builder/finalizeGame', { id: gameId.value })
        .then(() => {
          isSaving.value = false
          toast({
            component: ToastificationContent,
            props: {
              title: 'Игра финализирована',
              icon: 'CheckIcon',
              variant: 'success',
            },
          })
          router.push({ name: 'games' })
        })
        .catch(error => {
          isSaving.value = false
          const msg = error.response?.data?.message || 'Ошибка финализации'
          toast({
            component: ToastificationContent,
            props: {
              title: msg,
              icon: 'AlertTriangleIcon',
              variant: 'danger',
            },
          })
        })
    }

    const onExportSiq = () => {
      isExporting.value = true
      store.dispatch('game-builder/exportSiq', { id: gameId.value })
        .then(() => store.dispatch('game-builder/downloadSiq', { id: gameId.value }))
        .then(response => {
          isExporting.value = false
          const blob = new Blob([response.data], { type: 'application/octet-stream' })
          const url = window.URL.createObjectURL(blob)
          const link = document.createElement('a')
          link.href = url
          const disposition = response.headers['content-disposition']
          const filename = disposition
            ? disposition.match(/filename="?(.+?)"?$/)?.[1] || `game-${gameId.value}.siq`
            : `game-${gameId.value}.siq`
          link.setAttribute('download', filename)
          document.body.appendChild(link)
          link.click()
          link.remove()
          window.URL.revokeObjectURL(url)
          toast({
            component: ToastificationContent,
            props: {
              title: 'SIQ файл скачан',
              icon: 'CheckIcon',
              variant: 'success',
            },
          })
        })
        .catch(() => {
          isExporting.value = false
          toast({
            component: ToastificationContent,
            props: {
              title: 'Ошибка экспорта SIQ',
              icon: 'AlertTriangleIcon',
              variant: 'danger',
            },
          })
        })
    }

    const onDeleteGame = () => {
      if (!window.confirm('Удалить эту игру? Действие необратимо.')) return
      store.dispatch('game-builder/deleteGame', { id: gameId.value })
        .then(() => {
          toast({
            component: ToastificationContent,
            props: {
              title: 'Игра удалена',
              icon: 'CheckIcon',
              variant: 'success',
            },
          })
          router.push({ name: 'game-builder-list' })
        })
        .catch(() => {
          toast({
            component: ToastificationContent,
            props: {
              title: 'Ошибка удаления',
              icon: 'AlertTriangleIcon',
              variant: 'danger',
            },
          })
        })
    }

    return {
      createForm,
      gameId,
      gameData,
      currentRtId,
      currentStep,
      suggestedTopic,
      availableItems,
      suggestedMovie,
      framesSource,
      isLoading,
      isSaving,
      isLoadingTopic,
      isLoadingMovie,
      currentRtObject,
      progressPercent,
      onCreateGame,
      onTopicAccepted,
      onTopicSkipped,
      onQuestionAccepted,
      onMovieSkipped,
      onQuestionDeleted,
      onTopicRemoved,
      isExporting,
      onExportSiq,
      onFinalize,
      onDeleteGame,
    }
  },
}
</script>

<style lang="scss">
@import '@core/scss/vue/pages/page-blog.scss';
</style>
